import { styled } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../common/hooks';
import { useClickWrapAgreementsChecker } from '../common/hooks/useClickWrapAgreementsChecker';
import { useLogoutHandler } from '../common/hooks/useLogoutHandler';
import { Footer, Header } from '../components/molecules';
import { Typography } from '../components/atoms';
import { ClickWrapAgreement } from '../components/molecules/ClickWrapAgreement/ClickWrapAgreement';
import { callRunpipe, checkFFEnabled, getClientJourneyFlowType, getCurrentYear } from '../utils';
import { saveUserAgreementsSettings, setOpenDrawer } from '../features/global/globalSlice';
import { Agreement } from 'utils/types';
import { TenantSelector } from '../components/organisms/TenantSelector/TenantSelector';
import { useCMSContent } from '../common/hooks/useCmsContent';
import { updateUserInfo } from '../features/auth/authSlice';
import oktaAuth from '../services/configs/oktaAuth';
import { ADVISOR_ROUTES } from './routeConstants';
import { removeLoader, saveGoal, showLoader } from '../features/client-goals';
import { FEATURE_NAMES } from '../common/constants';
import AddIncomeSourcesDrawer from '../containers/add-income-sources-drawer';
import { AddInvestmentAccounts } from '../containers/add-investment-accounts';
import { GoalRecommendationType } from '../common/types';

const displaySidebarPaths = [ADVISOR_ROUTES.PLAN_DETAILS];

const PageContainer: any = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh'
}));

const StyledContainer: any = styled('div')(() => ({
    display: 'flex',
    flexGrow: 1
}));

const StyledMainContainer = styled('section')(() => ({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    paddingTop: '60px'
}));

const DisclaimerBanner = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.neutral.white,
    padding: '16px 40px 12px 40px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '12px'
}));

const DisclaimerBannerInner = styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between'
}));

export const RequiredAuth: React.FC = () => {
    const { authState } = useOktaAuth();
    // const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const cmsUtil = useCMSContent();
    const isAuthenticated = authState?.isAuthenticated;
    const user = useAppSelector((state) => state?.auth?.user);
    const global = useAppSelector((state) => state?.global);
    const clickWrapAgreements = useAppSelector((state) => state?.auth?.user?.clickWrapAgreements);
    const { pathname } = useLocation();
    const [activeDrawer, setActiveDrawer] = useState<null | 'incomeSources' | 'accounts'>(null);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const { handleLogout } = useLogoutHandler();
    const advIncludedFooterLinks = [
        {
            label: t('UPDATED_TERMS_OF_USE_AGREEMENT'),
            link: `${process.env.PUBLIC_URL}/assets/pdf/en/Digital_Advisor_Terms_of_Use.pdf`
        },
        {
            label: t('PRIVACY_NOTICE_TEXT'),
            link: 'https://www.franklintempletonglobal.com/privacy'
        },
        {
            label: t('ADV_TEXT'),
            link: `https://www.franklintempleton.com/help/client-relationship-summaries-for-retail-investors`
        }
    ];
    const userInfo = useAppSelector((state) => state?.auth?.user);
    const tenant = userInfo?.tenant || '';
    const isFlow1 = getClientJourneyFlowType(tenant) === 'DEFAULT';
    const isHantzTenant = userInfo?.isHantzTenant;
    const { openUserAgreementModal, openEulaAgreementModal } = useClickWrapAgreementsChecker();
    const goalDataRecomendation = adviceGoals?.recommendation?.find((rec: GoalRecommendationType) => {
        if (checkFFEnabled(FEATURE_NAMES['RECOMMENDATION_RECONCILIATION_FE_AEX-1848']))
            return (rec?.oneTimeTopUp || rec?.recomendedTenure || rec?.topUpAccumulation) && rec?.originalData;
    });

    const handleAgreementComplete = async () => {
        // Initialize agreements array old structure is replaced with empty array
        let agreements = clickWrapAgreements?.userAggrementAcceptedTimeStamp
            ? []
            : [...(clickWrapAgreements?.agreements || [])];

        const timestamp = dayjs().format('YYYYMMDDHHmmss');

        const updateAgreement = (agreementName: string, version: string | undefined): void => {
            const existingIndex = agreements.findIndex((el: Agreement) => el.agreementName === agreementName);

            const agreementData: Agreement = {
                agreementAccepted: true,
                agreementName,
                acceptedAgreementVersion: version,
                agreementAcceptedTimeStamp: timestamp
            };

            if (existingIndex !== -1) {
                agreements[existingIndex] = agreementData;
            } else {
                agreements.push(agreementData);
            }
        };

        if (global.globalConfig.termsOfUse && openUserAgreementModal) {
            if (isHantzTenant) {
                updateAgreement('UserAgreement', user?.hantz?.version);
            } else {
                updateAgreement('UserAgreement', user?.userAgreement?.version);
            }
        }

        if (global.globalConfig.eula && openEulaAgreementModal) {
            updateAgreement('EulaAgreement', user?.eula?.version);
        }

        if (agreements.length > 0) {
            let response = await dispatch(
                saveUserAgreementsSettings({
                    click_wrap_agreements: {
                        agreements
                    },
                    tenant: tenant
                })
            );

            if ((response.payload as any)?.response?.success) {
                await dispatch(
                    updateUserInfo({
                        ...userInfo,
                        cwa: {
                            ...userInfo.cwa,
                            [tenant]: { ...userInfo.cwa[tenant], clickWrapAgreements: { agreements } }
                        }
                    })
                );
            }
        }
    };

    const handleEditSuccess = async (deletedAccounts: string[] = [], formFields?: any[]) => {
        const formFieldsData = formFields || adviceGoals.formFields;
        if (formFieldsData.length > 0) {
            dispatch(showLoader());
            const newFormFields = formFieldsData.map((goal: any) => {
                return {
                    ...goal,
                    data: {
                        ...goal.data,
                        sources: {
                            ...goal.data.sources,
                            incomeSource: goal.data.sources.incomeSource.filter(
                                (source: any) => !deletedAccounts.includes(source.accountNumber)
                            ),
                            investmentSource: goal.data.sources.investmentSource.filter(
                                (source: any) => !deletedAccounts.includes(source.accountNumber)
                            )
                        }
                    }
                };
            });
            await dispatch(
                saveGoal({
                    ...{ ...adviceGoals, formFields: newFormFields },
                    global: {
                        editMode: true,
                        ...adviceGoals?.global,
                        ...(adviceGoals.global?.enablePDFGenerate && { enablePDFGenerate: false }),
                        ...(adviceGoals.global?.enablePlanDetails && { enablePlanDetails: false })
                    }
                })
            );
            dispatch(removeLoader());
        }
    };

    // Sign out if token renewal fails (e.g. refresh token revoked/expired)
    useEffect(() => {
        if (!isAuthenticated) return;
        if (isFlow1 && (global.openDrawer === 'incomeSources' || global.openDrawer === 'accounts')) {
            setActiveDrawer(global.openDrawer);
            // Reset the openDrawer state in the global slice after using it
            dispatch(setOpenDrawer(''));
        }
        const handleRenewError = (err: any) => {
            console.error('Okta token renewal failed, signing out', err);
            void oktaAuth.signOut();
        };
        oktaAuth.tokenManager.on('error', handleRenewError);
        return () => {
            oktaAuth.tokenManager.off('error', handleRenewError);
        };
    }, [isAuthenticated, isFlow1, global.openDrawer, dispatch]);

    // useEffect(() => {
    //     if (authState && !isAuthenticated) {
    //         const originalUri = toRelativeUrl(window.location.origin, window.location.href);
    //         oktaAuth.setOriginalUri(originalUri);
    //         navigate(originalUri);
    //     }
    // }, [authState, isAuthenticated]);

    if (!isAuthenticated) {
        return null;
    }

    if (user && Object.keys(user).length > 0 && !user.tenantSelected) {
        return <TenantSelector />;
    }

    if (global.showAgreementModal) {
        return (
            <ClickWrapAgreement
                title={t('AGREEMENTS')}
                open={global.showAgreementModal}
                agreements={[
                    ...(global.globalConfig.termsOfUse && openUserAgreementModal
                        ? [
                              {
                                  title: t('UPDATED_TERMS_OF_USE_AGREEMENT'),
                                  content: isHantzTenant
                                      ? user?.userAgreement?.text + user?.hantz?.text
                                      : user?.userAgreement?.text
                              }
                          ]
                        : []),
                    ...(global.globalConfig.eula && openEulaAgreementModal
                        ? [
                              {
                                  title: t('EULA'),
                                  content: user?.eula?.text
                              }
                          ]
                        : [])
                ]}
                onClose={handleLogout}
                onComplete={() => handleAgreementComplete()}
            />
        );
    }

    // console.log(pathname, 'location');
    // if (
    //     user.userRole !== 'admin' &&
    //     user.userRole !== 'ftadmin' &&
    //     !allowedPathsForAdvisor.includes(window.location.pathname)
    // ) {
    //     return <Navigate to="/clients" />;
    // }

    const advisorConsoleBanorteFooterPart1 =
        tenant === 'banortemex' ? cmsUtil.getContentAsElement('advisor_console.banorte_footer.part1') : null;

    return (
        <PageContainer>
            <Header />
            <StyledContainer>
                <StyledMainContainer>
                    {isFlow1 && (
                        <>
                            {displaySidebarPaths.includes(pathname) ? (
                                <>
                                    {activeDrawer === 'incomeSources' && (
                                        <AddIncomeSourcesDrawer
                                            setOpenDrawer={(flag: boolean) =>
                                                setActiveDrawer(flag ? 'incomeSources' : null)
                                            }
                                            openDrawer={activeDrawer === 'incomeSources'}
                                            mode="edit"
                                            onEditSuccess={handleEditSuccess}
                                            callRunpipe={callRunpipe}
                                        />
                                    )}
                                    {activeDrawer === 'accounts' && (
                                        <AddInvestmentAccounts
                                            setOpenDrawer={(flag: boolean) => setActiveDrawer(flag ? 'accounts' : null)}
                                            openDrawer={activeDrawer === 'accounts'}
                                            mode="edit"
                                            onEditSuccess={handleEditSuccess}
                                            callRunpipe={callRunpipe}
                                            goalDataRecomendation={goalDataRecomendation}
                                        />
                                    )}
                                </>
                            ) : null}
                        </>
                    )}
                    <div style={{ flexGrow: 1 }}>
                        <Outlet />
                    </div>

                    {advisorConsoleBanorteFooterPart1 && (
                        <DisclaimerBanner>
                            <DisclaimerBannerInner>
                                <Typography
                                    variant="body.caption"
                                    color="semantic.text.muted"
                                    data-testid="banorte-footer-description"
                                >
                                    {advisorConsoleBanorteFooterPart1}
                                </Typography>
                            </DisclaimerBannerInner>
                        </DisclaimerBanner>
                    )}

                    <Footer
                        primaryText={`${cmsUtil.getContentAsString('advisor_console.footer.part2', {
                            value1: getCurrentYear()
                        })}`}
                        links={advIncludedFooterLinks}
                        eulaVariant={true} // to hide the detailed footer
                        isPrimaryFooterDisplayed={false} // to show the primary footer
                        hideDisclaimerFooter={false}
                    />
                </StyledMainContainer>
            </StyledContainer>
            <Footer
                primaryText={`${cmsUtil.getContentAsString('advisor_console.footer.part2', {
                    value1: getCurrentYear()
                })}`}
                links={advIncludedFooterLinks}
                isPrimaryFooterDisplayed={true} // to show the primary footer
                hideDisclaimerFooter={true}
            />
        </PageContainer>
    );
};
